import React, { useState, useEffect } from 'react';
import styled from '@emotion/styled';
import { Only } from 'ui/components/elements/Only/Only';
import IconArrow from 'ui/images/top/icon_slidearrow.svg';
import '@splidejs/react-splide/css';
import { Splide, SplideSlide } from '@splidejs/react-splide';
import type { Splide as SplideInstance } from '@splidejs/splide';
import { mqMin } from 'utils/responsive';

const Container = styled.section<{ backgroundColor?: string }>`
    position: relative;
    padding-bottom: 60px;
    background-color: ${(props) => props.backgroundColor || 'var(--white)'};
`;

const MoviesWrapper = styled.div`
    width: 88.37209302325581%;
    margin: 0 auto;
    max-width: 1200px;
    position: relative;

    ${mqMin('NARROW')} {
        width: 100%;
    }
`;

const Arrows = styled.div`
    display: none;
    ${mqMin('NARROW')} {
        display: block;
        width: 100%;
        position: absolute;
        top: 40%;
        left: 0;
        right: 0;
        transform: translateY(-50%);
        z-index: 2;
        pointer-events: none;
    }
`;

const ArrowButton = styled.button`
    top: initial !important;
    ${mqMin('NARROW')} {
        width: 40px;
        height: 40px;
        border: none;
        background: none;
        padding: 0;
        transition: all 0.3s;
        pointer-events: auto;
        display: flex;
        align-items: center;
        justify-content: center;
        box-shadow: none;
    }
`;

const ArrowPrev = styled(ArrowButton)`
    position: absolute;
    left: 0px;
    transform: scale(-1, 1);
    z-index: 2;

    ${mqMin('STANDARD')} {
        left: -50px;
    }
`;

const ArrowNext = styled(ArrowButton)`
    position: absolute;
    right: 0px;
    z-index: 2;
    transform: initial !important;

    ${mqMin('STANDARD')} {
        right: -50px;
    }
`;

const VideoSlide = styled(SplideSlide)`
    width: 100%;
    border-radius: 12px;
    background-color: #fff;
    margin: 0 auto 20px auto;
    overflow: hidden;
    box-shadow: 0 4px 8px rgba(0, 0, 0, 0.1);

    ${mqMin('NARROW')} {
        width: 350px;
        height: 200px;
        margin: initial;
    }
`;

const SplideWrapper = styled.div`
    .splide__arrow {
        width: 40px;
        height: 40px;
        z-index: 10;
    }

    .splide__arrow--prev {
        left: 10px;
    }

    .splide__arrow--next {
        right: 10px;
    }

    .splide__pagination {
        bottom: -30px;
    }

    .splide__pagination__page {
        background: rgba(0, 0, 0, 0.3);
        margin: 0 5px;
    }

    .splide__pagination__page.is-active {
        background: var(--orange);
    }

    .splide__pagination {
        bottom: 0.1rem;
    }
`;

const VideoContainer = styled.div`
    position: relative;
    width: 100%;
    height: 0;
    padding-bottom: 56.25%;
    background-color: #000;
    border-radius: 12px;
    overflow: hidden;
    cursor: pointer;
    transition: transform 0.3s ease;

    &:hover {
        transform: scale(1.02);
    }

    iframe {
        position: absolute;
        top: 0;
        left: 0;
        width: 100%;
        height: 100%;
        border: none;
        border-radius: 12px;
        pointer-events: none;
    }
`;

const ModalOverlay = styled.div`
    position: fixed;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    background-color: rgba(0, 0, 0, 0.8);
    z-index: 1000;
    display: flex;
    justify-content: center;
    align-items: center;
    padding: 20px;
`;

const ModalVideoContainer = styled.div`
    position: relative;
    width: 90vw;
    height: 60vh;
    background-color: #000;
    border-radius: 12px;
    overflow: hidden;
    z-index: 1001;

    ${mqMin('NARROW')} {
        width: 80vw;
        height: 70vh;
    }

    iframe {
        position: absolute;
        top: 0;
        left: 0;
        width: 100%;
        height: 100%;
        border: none;
    }
`;

const CloseButton = styled.button`
    position: absolute;
    top: -50px;
    right: 0;
    background: rgba(0, 0, 0, 0.8);
    border: none;
    color: #fff;
    font-size: 2rem;
    cursor: pointer;
    padding: 10px 15px;
    border-radius: 50%;
    z-index: 1002;
    width: 50px;
    height: 50px;
    display: flex;
    align-items: center;
    justify-content: center;

    &:hover {
        background: rgba(0, 0, 0, 0.9);
    }
`;

const H2 = styled.h2`
    text-align: center;
    font-size: 1.25rem; /*20px*/
    line-height: 1.7em;
    letter-spacing: 0.05em;
    padding: 57px 0 28px 0;

    strong {
        color: var(--orange);
    }

    ${mqMin('NARROW')} {
        text-align: center;
        font-size: 1.5rem; /*24px*/
        line-height: 1.8em;
        letter-spacing: 0.1em;
        padding: 60px 0 55px 0;
    }
`;

const VideoData = [
    {
        id: 1,
        videoId: 'hwFMIBPNQUs',
    },
    {
        id: 2,
        videoId: 'ADuynsj_q5c',
    },
    {
        id: 3,
        videoId: 'QI4aTA6zWKk',
    },
    {
        id: 4,
        videoId: 'IAfcIaca4W8',
    },
];

interface MoviesProps {
    backgroundColor?: string;
    title?: string;
}

export const Movies: React.FC<MoviesProps> = ({ backgroundColor, title }) => {
    const [splideInstance, setSplideInstance] = useState<SplideInstance | null>(null);
    const [selectedVideo, setSelectedVideo] = useState<string | null>(null);

    const handleVideoClick = (videoId: string) => {
        setSelectedVideo(videoId);
    };

    const handleCloseModal = () => {
        setSelectedVideo(null);
    };

    useEffect(() => {
        const handleKeyDown = (event: KeyboardEvent) => {
            if (event.key === 'Escape' && selectedVideo) {
                handleCloseModal();
            }
        };

        if (selectedVideo) {
            document.addEventListener('keydown', handleKeyDown);
            document.body.style.overflow = 'hidden';
        }

        return () => {
            document.removeEventListener('keydown', handleKeyDown);
            document.body.style.overflow = 'unset';
        };
    }, [selectedVideo]);

    return (
        <Container backgroundColor={backgroundColor}>
            <H2 className="fadein fadein_y">
                {title ? (
                    title
                ) : (
                    <>
                        貴方にぴったりの
                        <Only sp>
                            <br />
                        </Only>
                        仲介会社を選ぶために、
                        <br />
                        <strong>
                            契約前の複数社との面談
                            <Only sp>
                                <br />
                            </Only>
                            （セカンドオピニオン）
                        </strong>
                        が重要です。
                    </>
                )}
            </H2>
            <MoviesWrapper className="fadein fadein_y">
                <Only pc>
                    <Arrows>
                        <ArrowPrev onClick={() => splideInstance?.go('<')}>
                            <img src={IconArrow} alt="前の動画" />
                        </ArrowPrev>
                        <ArrowNext onClick={() => splideInstance?.go('>')}>
                            <img src={IconArrow} alt="次の動画" />
                        </ArrowNext>
                    </Arrows>

                    <Splide
                        className="splide"
                        role="group"
                        aria-label="動画リスト"
                        onMounted={(splide) => {
                            setSplideInstance(splide);
                        }}
                        options={{
                            perPage: 1,
                            fixedWidth: '350px',
                            gap: '36px',
                            pagination: false,
                            hasArrows: true,
                            arrows: false,
                            type: 'loop',
                            rewind: true,
                            breakpoints: {
                                768: {
                                    perPage: 1,
                                    fixedWidth: '350px',
                                },
                                1024: {
                                    perPage: 2,
                                    fixedWidth: '350px',
                                },
                                1200: {
                                    perPage: 3,
                                    fixedWidth: '350px',
                                },
                            },
                        }}
                    >
                        {VideoData.map((video) => (
                            <VideoSlide key={video.id}>
                                <VideoContainer onClick={() => handleVideoClick(video.videoId)}>
                                    <iframe
                                        src={`https://www.youtube.com/embed/${video.videoId}`}
                                        title="YouTube動画"
                                        allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                                        allowFullScreen
                                    />
                                </VideoContainer>
                            </VideoSlide>
                        ))}
                    </Splide>
                </Only>

                <Only sp>
                    <SplideWrapper>
                        <Splide
                            className="splide"
                            role="group"
                            aria-label="動画リスト"
                            options={{
                                perPage: 1,
                                gap: '20px',
                                pagination: true,
                                arrows: true,
                                padding: '20px',
                                type: 'loop',
                                rewind: true,
                                breakpoints: {
                                    480: {
                                        padding: '40px',
                                    },
                                },
                            }}
                        >
                            {VideoData.map((video) => (
                                <VideoSlide key={video.id}>
                                    <VideoContainer onClick={() => handleVideoClick(video.videoId)}>
                                        <iframe
                                            src={`https://www.youtube.com/embed/${video.videoId}`}
                                            title="YouTube動画"
                                            allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                                            allowFullScreen
                                        />
                                    </VideoContainer>
                                </VideoSlide>
                            ))}
                        </Splide>
                    </SplideWrapper>
                </Only>
            </MoviesWrapper>
            {selectedVideo && (
                <ModalOverlay onClick={handleCloseModal}>
                    <ModalVideoContainer onClick={(e) => e.stopPropagation()}>
                        <CloseButton onClick={handleCloseModal}>×</CloseButton>
                        <iframe
                            src={`https://www.youtube.com/embed/${selectedVideo}?autoplay=1`}
                            title="YouTube動画"
                            allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                            allowFullScreen
                        />
                    </ModalVideoContainer>
                </ModalOverlay>
            )}
        </Container>
    );
};
